import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import AnalyticsApiService, {
  PostObservabilityOverviewTimeseriesPoint,
  PostObservabilityPostRepliesTimeseriesPoint,
} from '../../services/analyticsApi';
import { DateRangePicker } from './DateRangePicker';
import { DateRange } from 'react-day-picker';
import { parseLocalDate } from '../../utils/dateParser';
import { buildDailySeries, sumByUtcDay } from '../../utils/dailyTimeseries';

interface PostReplyVolumeChartProps {
  className?: string;
  days?: 7 | 14 | 30;
  showControls?: boolean;
}

export function PostReplyVolumeChart({ className, days: propDays, showControls = true }: PostReplyVolumeChartProps) {
  const [data, setData] = useState<
    Array<{
      date: string; // YYYY-MM-DD
      impressions: number;
      replies: number;
      impressions_over_replies: number;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    const start = new Date();
    start.setDate(start.getDate() - (propDays || 7));
    start.setHours(0, 0, 0, 0);

    return { from: start, to: end };
  });

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!dateRange?.from || !dateRange?.to) {
        setData([]);
        return;
      }

      const startOfDay = new Date(dateRange.from);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(dateRange.to);
      endOfDay.setHours(23, 59, 59, 999);

      const fromIso = startOfDay.toISOString();
      const toIso = endOfDay.toISOString();

      const [overviewTs, repliesTs] = await Promise.all([
        AnalyticsApiService.getPostObservabilityOverviewTimeseries(fromIso, toIso),
        AnalyticsApiService.getPostObservabilityPostRepliesTimeseries(fromIso, toIso),
      ]);

      const impressionsByDay = sumByUtcDay<PostObservabilityOverviewTimeseriesPoint>(
        overviewTs.points || [],
        (p) => p.bucket_start,
        (p) => Number(p.impressions || 0)
      );

      const repliesByDay = sumByUtcDay<PostObservabilityPostRepliesTimeseriesPoint>(
        repliesTs.points || [],
        (p) => p.bucket_start,
        (p) => Number(p.totalRepliesToPosts || 0)
      );

      const daily = buildDailySeries(
        fromIso,
        toIso,
        {
          impressions: impressionsByDay,
          replies: repliesByDay,
          impressions_over_replies: {},
        },
        { impressions: 0, replies: 0, impressions_over_replies: 0 }
      ).map((row) => {
        const replies = row.replies || 0;
        const impressions = row.impressions || 0;
        const ratio = replies > 0 ? impressions / replies : 0;
        return {
          ...row,
          impressions_over_replies: ratio,
        };
      });

      setData(daily);
    } catch (e) {
      console.error('Failed to fetch post reply volume:', e);
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.from, dateRange?.to]);

  useEffect(() => {
    const id = setInterval(fetchData, 3600000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.from, dateRange?.to]);

  const formatXAxis = (dateStr: string) => {
    const d = parseLocalDate(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const d = parseLocalDate(label);
    const point = payload?.[0]?.payload as
      | { date: string; impressions: number; replies: number; impressions_over_replies: number }
      | undefined;
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-sky-200 dark:border-sky-700 rounded-xl p-3 shadow-xl">
        <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xs mb-2">
          {d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
        <p className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-sky-600 dark:text-sky-400 text-lg">
          {Number(point?.impressions_over_replies || 0).toFixed(2)} impressions / reply
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{(point?.impressions || 0).toLocaleString()} impressions</p>
        <p className="text-xs text-gray-600 dark:text-gray-400">{(point?.replies || 0).toLocaleString()} replies</p>
      </div>
    );
  };

  const totalImpressions = data.reduce((sum, d) => sum + (d.impressions || 0), 0);
  const totalReplies = data.reduce((sum, d) => sum + (d.replies || 0), 0);
  const hasData = data.length > 0;
  const avgRatio = hasData ? data.reduce((acc, d) => acc + (d.impressions_over_replies || 0), 0) / data.length : 0;
  const maxRatio = hasData ? Math.max(...data.map((d) => d.impressions_over_replies || 0)) : 0;

  return (
    <Card
      className={`bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200/50 dark:border-gray-800/50 shadow-xl overflow-hidden ${className}`}
    >
      <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-sky-100 dark:bg-sky-900/50 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-sky-600 dark:text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 8h10M7 12h6m-6 8l-4-4V4a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H7z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100">
                  Impressions over replies
                </h3>
                <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
                  Daily impressions divided by daily replies
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 mt-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Impressions</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-sky-600 dark:text-sky-400">
                  {hasData ? totalImpressions.toLocaleString() : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Replies</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-700 dark:text-gray-300">
                  {hasData ? totalReplies.toLocaleString() : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Avg Ratio</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-700 dark:text-gray-300">
                  {hasData ? avgRatio.toFixed(2) : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Peak Ratio</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-700 dark:text-gray-300">
                  {hasData ? maxRatio.toFixed(2) : '—'}
                </p>
              </div>
            </div>
          </div>

          {showControls && (
            <DateRangePicker value={dateRange} onChange={setDateRange} showDateText={false} className="w-auto" />
          )}
        </div>
      </div>

      <div className="p-6 pt-4">
        {isLoading ? (
          <Skeleton className="h-64 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
        ) : error ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-red-500 dark:text-red-400">{error}</p>
          </div>
        ) : !hasData ? (
          <div className="h-64 flex items-center justify-center rounded-xl border border-dashed border-gray-200/60 dark:border-gray-700/60 bg-gray-50/30 dark:bg-gray-800/10">
            <div className="text-center px-6">
              <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100">
                No post replies in this range
              </p>
              <p className="text-xs font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400 mt-1">
                Try selecting an earlier date range.
              </p>
            </div>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="postReplyVolGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0284c7" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#0284c7" stopOpacity={0.6} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                  stroke="#e5e7eb"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                  stroke="#e5e7eb"
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="impressions_over_replies"
                  fill="url(#postReplyVolGradient)"
                  radius={[8, 8, 0, 0]}
                  maxBarSize={60}
                  animationDuration={1000}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}


